import { AlertTriangle } from 'lucide-react'
import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import type { AllowedApp } from '@/types/api'

const APP_LABELS: Record<AllowedApp, string> = {
  compass: 'Compass',
  ogsm: 'Legacy OGSM',
  'deal-review': 'Deal Review',
}

const APP_ORDER: AllowedApp[] = ['compass', 'ogsm', 'deal-review']

const APP_OPTIONS: Array<{ value: AllowedApp; label: string }> = APP_ORDER.map(
  (value) => ({ value, label: APP_LABELS[value] })
)

export function sortAllowedApps(apps: readonly AllowedApp[]): AllowedApp[] {
  return [...apps].sort(
    (left, right) => APP_ORDER.indexOf(left) - APP_ORDER.indexOf(right)
  )
}

export function formatAllowedAppsSummary(apps: readonly AllowedApp[]): string {
  const labels = sortAllowedApps(apps).map((app) => APP_LABELS[app])

  return labels.length > 0 ? labels.join(' + ') : 'No access'
}

interface UserAppAccessFieldsProps {
  allowedApps: AllowedApp[]
  onAllowedAppsChange: (allowedApps: AllowedApp[]) => void
  initialAllowedApps?: readonly AllowedApp[]
  context?: 'invite' | 'edit'
  includeDealReview?: boolean
  className?: string
}

export function UserAppAccessFields({
  allowedApps,
  onAllowedAppsChange,
  initialAllowedApps,
  context = 'invite',
  includeDealReview = false,
  className,
}: UserAppAccessFieldsProps) {
  const options = includeDealReview
    ? APP_OPTIONS
    : APP_OPTIONS.filter((app) => app.value !== 'deal-review')
  const toggleAllowedApp = (app: AllowedApp, on: boolean) => {
    const nextAllowedApps = on
      ? sortAllowedApps([...allowedApps, app])
      : allowedApps.filter((value) => value !== app)

    onAllowedAppsChange(nextAllowedApps)
  }

  const removedApps =
    context === 'edit' && initialAllowedApps
      ? initialAllowedApps.filter((app) => !allowedApps.includes(app))
      : []

  // Losing deal review hides a section of Compass; it does not sign them out.
  const removedSignInApps = removedApps.filter((app) => app !== 'deal-review')
  const dealReviewRemoved = removedApps.includes('deal-review')

  const helpText =
    context === 'invite'
      ? "Apps they'll be able to sign into after accepting the invite."
      : 'Changing this affects the user on their next session.'

  return (
    <fieldset className={cn('flex flex-col', className)}>
      <legend className="mb-1 block text-[11px] font-medium uppercase tracking-[0.14em] text-primary/70">
        App access
      </legend>
      <Typography variant="body-sm" className="mb-2.5 text-primary/70">
        {helpText}
      </Typography>

      <div className="flex flex-col gap-2">
        {options.map((app) => {
          const on = allowedApps.includes(app.value)
          return (
            <button
              key={app.value}
              type="button"
              onClick={() => toggleAllowedApp(app.value, !on)}
              aria-pressed={on}
              className={cn(
                'group flex w-full cursor-pointer items-center justify-between rounded-lg border px-4 py-3 text-left transition-all',
                on
                  ? 'border-secondary bg-secondary/[0.04]'
                  : 'border-primary/15 bg-background hover:border-primary/40'
              )}
            >
              <Typography
                variant="body"
                as="span"
                className="font-medium text-primary"
              >
                {app.label}
              </Typography>
              <span
                className={cn(
                  'relative h-[18px] w-[30px] shrink-0 rounded-full transition-colors',
                  on ? 'bg-secondary' : 'bg-primary/15'
                )}
              >
                <span
                  className={cn(
                    'absolute left-[2px] top-[2px] h-[14px] w-[14px] rounded-full bg-white shadow-sm transition-transform',
                    on && 'translate-x-[12px]'
                  )}
                />
              </span>
            </button>
          )
        })}
      </div>

      {allowedApps.length === 0 && (
        <Typography
          variant="body-sm"
          className="mt-2 text-[11px] text-destructive"
        >
          Select at least one app.
        </Typography>
      )}

      {removedApps.length > 0 && (
        <div className="mt-3 flex gap-2.5 rounded-r-md border-l-[3px] border-secondary bg-secondary/[0.08] px-3.5 py-3">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-secondary" />
          <Typography
            variant="body-sm"
            as="span"
            className="text-[12.5px] leading-snug text-primary"
          >
            <span className="font-semibold">
              Removing {formatAppList(removedApps)} access.
            </span>{' '}
            {removedSignInApps.length > 0 && (
              <>
                They&apos;ll be signed out of {formatAppList(removedSignInApps)}{' '}
                on their next session and lose access to any work in progress
                there.{' '}
              </>
            )}
            {dealReviewRemoved && (
              <>
                Deal Review disappears from their Compass sidebar on their next
                session, and the screens stop answering.
              </>
            )}
          </Typography>
        </div>
      )}
    </fieldset>
  )
}

function formatAppList(apps: readonly AllowedApp[]): string {
  const labels = sortAllowedApps(apps).map((app) => APP_LABELS[app])
  if (labels.length === 0) return ''
  if (labels.length === 1) return labels[0]
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
}
